<template>
    <div class="position-relative main-wrapper" :class="props.disabled && 'disabled'">
        <ContainerBox ref="input-container" :border="containerBoxBorder" :borderRadius="containerBorderRadius"
            class="container-wrapper" :active="state.isFocused">
            <div class="slot-before d-flex align-items-center justify-content-center">
                <slot name="before" />
            </div>
            <input :id="props.id" :disabled="props.disabled" ref="input" @input="(e) => handleInput(e)"
                @change="(e) => handleChange(e)" @focus="(e) => handleFocus(e)" @blur="(e) => handleBlur(e)"
                class="main-input form-control shadow-none no-autofill" :type="props.inputType ?? 'text'" :value="props.value"
                :placeholder="state.isFocused ? '' : props.placeholder" style="border: none; padding: 15px 25px"
                :style="props.inputStyles">
            <div class="slot-after d-flex align-items-center justify-content-center">
                <slot name="after" />
            </div>
        </ContainerBox>
        <div v-if="state.isFocused || props.value" class="upper-inscription bg-primary-white text-blue-800"
            :style="{ background: props.upperInscriptionBackground }">
            {{ props.placeholder }}
        </div>
    </div>
</template>

<script lang="ts" setup>
import { defineProps, defineEmits, reactive, withDefaults } from 'vue'
import ContainerBox from '@/components/LoginRegister/common/ContainerBox/v1/ContainerBox.vue'

interface Props {
    id?: string
    value: string
    disabled?: boolean
    placeholder?: string
    upperInscriptionBackground?: string
    inputStyles?: { [key: string]: string }
    containerBoxBorder?: string
    containerBorderRadius?: string
    inputType?: string,
    allowedType?: "number" | "text"
}
interface Emits {
    (e: 'change', value: string): void
    (e: 'input', value: string): void
}

// state
const state = reactive({
    isFocused: false,
})

// props
const props = withDefaults(defineProps<Props>(), {
    value: "",
})

// emit
const emit = defineEmits<Emits>()

// handlers
// handlers
const handleInput = (e: InputEvent) => {
    if (props.disabled) return
    const inputElement = e.target as HTMLInputElement;
    let inputValue = inputElement.value;

    // Conditionally filter input based on allowedType
    if (props.allowedType === 'number') {
        // Replace non-numeric characters to enforce numeric input
        inputValue = inputValue.replace(/\D/g, '');
        inputElement.value = inputValue;  // Set the sanitized value back to the input
    }

    emit('input', inputValue);
}

const handleChange = (e: InputEvent) => {
    if (props.disabled) return
    emit('change', e.target.value)
}
const handleFocus = (e: InputEvent) => {
    if (props.disabled) return
    state.isFocused = true
}
const handleBlur = (e: InputEvent) => {
    state.isFocused = false
}

</script>

<style  scoped>
.upper-inscription {
    position: absolute;
    left: 10px;
    top: 0;
    transform: translateY(-50%);
    padding-left: 8px;
    padding-right: 8px;
    font-size: 12px;
    font-weight: 600;
}

.container-wrapper {
    display: flex;
    position: relative;
    transition: box-shadow 100ms ease;
    overflow: hidden;
}

input.focus {
    border: 1px solid var(--primary-blue);
    box-shadow: 0px 0px 0px 4px var(--blue-100), 0px 0px 0px 2px rgb(0 0 0 / 25%);
}

.no-autofill:-webkit-autofill,
.no-autofill:-internal-autofill-selected,
.no-autofill:-webkit-autofill:active,
.no-autofill:-webkit-autofill:hover,
.no-autofill:-webkit-autofill:focus {
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  -webkit-animation-delay: 0s;
  -webkit-animation-name: none;
  -webkit-animation-fill-mode: none;
  background-color: transparent !important;
}

.form-control:disabled {
    background-color: transparent !important;
    color: var(--grey-800) !important;
    cursor: not-allowed !important;
}

/* remove arrows on number input */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.no-autofill {
  -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
          box-shadow: 0 0 0px 1000px transparent inset !important;
}

input[type=number] {
    -moz-appearance: textfield;
}
</style>
